const memoryStore = {};

const getStorage = (type) => {
  try {
    if (typeof window === 'undefined') return null;
    return window[type] || null;
  } catch (_e) {
    return null;
  }
};

const safeGetItem = (type, key) => {
  const storage = getStorage(type);
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch (_e) {
    return null;
  }
};

const safeSetItem = (type, key, value) => {
  const storage = getStorage(type);
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (_e) {
    return false;
  }
};

const safeRemoveItem = (type, key) => {
  const storage = getStorage(type);
  if (!storage) return false;
  try {
    storage.removeItem(key);
    return true;
  } catch (_e) {
    return false;
  }
};

const safeParseJson = (raw) => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
};

const getAuthFromStorage = (type) => {
  const userRaw = safeGetItem(type, 'user');
  const token = safeGetItem(type, 'token');
  if (!userRaw || !token) return null;
  const user = safeParseJson(userRaw);
  if (!user) return null;
  return { user, token, source: type };
};

export const getRememberMe = () => safeGetItem('localStorage', 'rememberMe') === 'true';

export const getStoredAuth = () => {
  const localAuth = getAuthFromStorage('localStorage');
  if (localAuth) return localAuth;

  const sessionAuth = getAuthFromStorage('sessionStorage');
  if (sessionAuth) return sessionAuth;

  if (memoryStore.user && memoryStore.token) {
    return { user: memoryStore.user, token: memoryStore.token, source: 'memory' };
  }

  return null;
};

export const getStoredToken = () => getStoredAuth()?.token || null;

/**
 * `commandPriority` из payload проектного JWT (если в Redux/local user ещё 0).
 */
export const getCommandPriorityFromAccessToken = () => {
  const t = getStoredToken();
  if (!t || typeof t !== 'string') return null;
  const parts = t.split('.');
  if (parts.length < 2) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padding = b64.length % 4;
    const padded =
      padding === 0 ? b64 : b64 + '='.repeat(4 - padding);
    const json = atob(padded);
    const payload = JSON.parse(json);
    const p = Number(
      payload.commandPriority ?? payload.commandpriority ?? payload.cp,
    );
    if (!Number.isFinite(p)) return null;
    return Math.max(0, Math.min(9, Math.trunc(p)));
  } catch {
    return null;
  }
};

export const getStoredUser = () => getStoredAuth()?.user || null;

export const setStoredToken = (token, rememberMe) => {
  const primary = rememberMe ? 'localStorage' : 'sessionStorage';
  const secondary = rememberMe ? 'sessionStorage' : 'localStorage';
  const primaryOk = safeSetItem(primary, 'token', token);
  if (primaryOk) {
    safeRemoveItem(secondary, 'token');
    return true;
  }
  const fallbackOk = safeSetItem(secondary, 'token', token);
  return fallbackOk;
};

export const storeAuth = ({ user, token, rememberMe }) => {
  const primary = rememberMe ? 'localStorage' : 'sessionStorage';
  const secondary = rememberMe ? 'sessionStorage' : 'localStorage';
  const userPayload = JSON.stringify(user || {});
  const primaryUserOk = safeSetItem(primary, 'user', userPayload);
  const primaryTokenOk = safeSetItem(primary, 'token', token || '');
  const primaryOk = primaryUserOk && primaryTokenOk;

  if (primaryOk) {
    safeRemoveItem(secondary, 'user');
    safeRemoveItem(secondary, 'token');
    safeSetItem('localStorage', 'rememberMe', rememberMe ? 'true' : 'false');
    return { storedIn: primary, fallback: false };
  }

  // Fallback to session storage if local storage is blocked/unavailable.
  const fallbackUserOk = safeSetItem(secondary, 'user', userPayload);
  const fallbackTokenOk = safeSetItem(secondary, 'token', token || '');
  safeRemoveItem(primary, 'user');
  safeRemoveItem(primary, 'token');
  safeSetItem('localStorage', 'rememberMe', 'false');
  if (!fallbackUserOk || !fallbackTokenOk) {
    memoryStore.user = user || null;
    memoryStore.token = token || null;
  }
  return { storedIn: secondary, fallback: true };
};

export const clearAuthStorage = () => {
  safeRemoveItem('localStorage', 'token');
  safeRemoveItem('localStorage', 'user');
  safeRemoveItem('localStorage', 'rememberMe');
  safeRemoveItem('sessionStorage', 'token');
  safeRemoveItem('sessionStorage', 'user');
  memoryStore.user = null;
  memoryStore.token = null;
};


